import { ColumnConfig } from 'components/DataGrid';
import ColumnSelector from 'components/DataGrid/ColumnSelector';
import ModalDialog from 'components/ModalDialog';
import React, { useMemo } from 'react';
import { Button } from 'reactstrap';

import { orderKPISections } from './helper';
import { KPIDashboardVisibilityPreferences, KPISection } from './types';

interface CustomizeDashboardModalProps {
  isOpen: boolean;
  toggle: () => void;
  sections: KPISection[];
  preferences: KPIDashboardVisibilityPreferences;
  onSave: (preferences: KPIDashboardVisibilityPreferences) => void;
}

const CustomizeDashboardModal = ({
  isOpen,
  toggle,
  sections,
  preferences,
  onSave,
}: CustomizeDashboardModalProps) => {
  const orderedSections = useMemo(
    () => orderKPISections(sections, preferences.sectionOrder),
    [preferences.sectionOrder, sections],
  );
  const columns = useMemo<ColumnConfig[]>(
    () =>
      orderedSections.map((section) => ({
        key: section.key,
        label: section.label,
      })),
    [orderedSections],
  );
  const enabledSectionKeys = useMemo(
    () =>
      orderedSections
        .filter(
          (section) => !preferences.hiddenSectionKeys.includes(section.key),
        )
        .map((section) => section.key),
    [orderedSections, preferences.hiddenSectionKeys],
  );

  const handleDisplayOptionsChange = (enabledKeys: string[]) => {
    const enabledKeySet = new Set(enabledKeys);
    const hiddenSectionKeys = sections
      .filter((section) => !enabledKeySet.has(section.key))
      .map((section) => section.key);
    const hiddenKeysInCurrentOrder = orderedSections
      .filter((section) => !enabledKeySet.has(section.key))
      .map((section) => section.key);

    // The shared selector returns visible items in the selected order. Append
    // hidden KPIs so re-enabling one retains a deterministic dashboard order.
    onSave({
      ...preferences,
      hiddenSectionKeys,
      hiddenMetricKeys: [],
      sectionOrder: [...enabledKeys, ...hiddenKeysInCurrentOrder],
    });
  };

  if (!isOpen) return null;

  return (
    <ModalDialog
      title="Display Options"
      subTitle={<p>Customize KPI display and order</p>}
      childComponent={
        <ColumnSelector
          columns={columns}
          enabledColumnKeys={enabledSectionKeys}
          defaultColumnKeys={sections.map((section) => section.key)}
          onColumnToggle={handleDisplayOptionsChange}
        />
      }
      onClose={toggle}
      customFooter={
        <>
          <Button
            color="primary"
            type="button"
            onClick={() =>
              onSave({
                ...preferences,
                hiddenSectionKeys: [],
                hiddenMetricKeys: [],
                sectionOrder: sections.map((section) => section.key),
              })
            }
          >
            Reset To Default
          </Button>
          <Button color="secondary" type="button" onClick={toggle}>
            Close
          </Button>
        </>
      }
    />
  );
};

export default CustomizeDashboardModal;
